/* eslint-disable jsx-a11y/label-has-associated-control */
import { DropdownSelect } from '@components/common/DropdownSelect';
import { useAppSelector } from '@hooks/useRedux';
import { useUpdateProperty } from '@hooks/accommodation';
import { selectAccommodationProperty } from '@store/accommodation';
import { selectUserCurrencyPrefix } from '@store/auth/authSelectors';
import { Save } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { TIMEZONES } from 'utils/constants';
import { RatePlansSection } from './RatePlansSection';

export const PropertySettingsPage: React.FC = () => {
  const property = useAppSelector(selectAccommodationProperty);
  const userCurrencyPrefix = useAppSelector(selectUserCurrencyPrefix);
  const updateProperty = useUpdateProperty();
  const isSubmitting = updateProperty.isPending;

  const [form, setForm] = useState({
    name: '',
    address: '',
    timezone: 'UTC',
    currency: userCurrencyPrefix,
    check_in_time: '14:00',
    check_out_time: '11:00',
    cancellation_policy: '',
  });

  useEffect(() => {
    if (property) {
      setForm({
        name: property.name,
        address: property.address ?? '',
        timezone: property.timezone,
        currency: property.currency,
        check_in_time: property.check_in_time,
        check_out_time: property.check_out_time,
        cancellation_policy: property.cancellation_policy ?? '',
      });
    }
  }, [property]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!property) return;
    updateProperty.mutate({
      id: property.id,
      updates: {
        ...form,
        address: form.address || null,
        cancellation_policy: form.cancellation_policy || null,
      },
    });
  };

  if (!property) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">
        Loading property…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Property details */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6">
        <h3 className="mb-5 text-sm font-semibold text-gray-900">
          Property details
        </h3>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Property name *
              </label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Address
              </label>
              <input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              />
            </div>

            <DropdownSelect
              label="Timezone"
              value={form.timezone}
              onChange={(v) => setForm({ ...form, timezone: v })}
              options={TIMEZONES.map((tz) => ({ value: tz, label: tz }))}
              searchable
            />

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Currency
              </label>
              <input
                value={form.currency}
                onChange={(e) =>
                  setForm({ ...form, currency: e.target.value.toUpperCase() })
                }
                maxLength={3}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Default check-in time
              </label>
              <input
                type="time"
                value={form.check_in_time}
                onChange={(e) =>
                  setForm({ ...form, check_in_time: e.target.value })
                }
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Default check-out time
              </label>
              <input
                type="time"
                value={form.check_out_time}
                onChange={(e) =>
                  setForm({ ...form, check_out_time: e.target.value })
                }
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Cancellation policy
              </label>
              <textarea
                rows={3}
                value={form.cancellation_policy}
                onChange={(e) =>
                  setForm({ ...form, cancellation_policy: e.target.value })
                }
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
                placeholder="e.g. Full refund if cancelled 48 hours before check-in."
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 rounded-xl bg-pos-primary px-5 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
            >
              <Save size={14} />
              {isSubmitting ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </form>
      </div>

      {/* Rate plans */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6">
        <RatePlansSection />
      </div>
    </div>
  );
};
